// src/components/Profile/OrderDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getStatusStyle, statusSteps, getStatusBgColor } from '../../utils/profileHelpers';
import { getCustomizationPreview } from '../../utils/orderHelpers';
import { formatDate } from '../../utils/profileHelpers';
import OrderTimelineModal from './OrderTimelineModal';
import CancelOrderModal from './CancelOrderModal';
import ReturnReplaceModal from './ReturnReplaceModal';
import RatingModal from './RatingModal';

export default function OrderDetailsModal({
    order,
    onClose,
    onViewImage,
    onEditItemImage,
    expandedItems,
    toggleExpand,
    getImageUrl,
    editingOrderItemId,
    orderItemNewImages,
    setOrderItemNewImages,
    setEditingOrderItemId,
    orderUploadingImages,
    handleOrderItemImageSave,
    showCancelModal,
    setShowCancelModal,
    cancellationReason,
    setCancellationReason,
    isCancelling,
    cancelError,
    setCancelError,
    handleCancelOrder,
    token,
    refreshOrder,
}) {
    const [showTimeline, setShowTimeline] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Return/Replace states
    const [currentItem, setCurrentItem] = useState(null);
    const [reason, setReason] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [showReturnPopup, setShowReturnPopup] = useState(false);
    const [showReplacePopup, setShowReplacePopup] = useState(false);

    // Local order state for instant UI updates
    const [currentOrder, setCurrentOrder] = useState(order);

    // Sync with order prop changes
    useEffect(() => {
        setCurrentOrder(order);
    }, [order]);

    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const canCancel = !['Dispatched', 'Shipped', 'Out for Delivery', 'Delivered', 'Cancelled'].includes(currentOrder.status);
    const statusStyle = getStatusStyle(currentOrder.status);

    // Update item status locally
    const updateItemStatus = (itemId, newStatus) => {
        setCurrentOrder(prevOrder => {
            const updatedItems = prevOrder.items.map(item =>
                item.id === itemId ? { ...item, status: newStatus } : item
            );
            return { ...prevOrder, items: updatedItems };
        });
    };


    // Review modal states
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [currentReviewItem, setCurrentReviewItem] = useState(null);
    const [submittedReviews, setSubmittedReviews] = useState({});
    const [loadingReviews, setLoadingReviews] = useState({});

    useEffect(() => {
        if (currentOrder.status === 'Delivered' && currentOrder.items) {
            currentOrder.items.forEach(item => fetchExistingReview(item.id));
        }
    }, [currentOrder.id, currentOrder.status]);

    const fetchExistingReview = async (orderItemId) => {
        setLoadingReviews(prev => ({ ...prev, [orderItemId]: true }));
        try {
            const resp = await axios.get(
                `${API_BASE_URL}/reviews/orderitem/${orderItemId}/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (resp.data) {
                setSubmittedReviews(prev => ({
                    ...prev,
                    [orderItemId]: {
                        id: resp.data.id,
                        rating: resp.data.rating,
                        description: resp.data.description,
                        images: resp.data.images?.map(img => img.image_url || img.image) || [],
                        date: resp.data.created_at
                    }
                }));
            }
        } catch (e) {
            // 404 is OK: no review yet
        } finally {
            setLoadingReviews(prev => ({ ...prev, [orderItemId]: false }));
        }
    };

    const handleReviewSubmitted = (orderItemId, reviewData) => {
        setSubmittedReviews(prev => ({ ...prev, [orderItemId]: reviewData }));
        setShowRatingModal(false);
        setCurrentReviewItem(null);
    };
    // Button handlers
    const handleReturnClick = (item) => {
        setCurrentItem(item);
        setReason('');
        setSubmitError('');
        setShowReturnPopup(true);
    };

    const handleReplaceClick = (item) => {
        setCurrentItem(item);
        setReason('');
        setSubmitError('');
        setShowReplacePopup(true);
    };

    const handleCloseReturnPopup = () => {
        setShowReturnPopup(false);
        setCurrentItem(null);
    };

    const handleCloseReplacePopup = () => {
        setShowReplacePopup(false);
        setCurrentItem(null);
    };

    // Submit handlers with instant UI update
    const submitReturnRequest = async () => {
        if (!reason.trim()) {
            setSubmitError('Please provide a reason.');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(
                `${API_BASE_URL}/order-items/${currentItem.id}/request-return/`,
                { reason: reason.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowReturnPopup(false);
            alert('Return request submitted successfully!');
            updateItemStatus(currentItem.id, 'Return Requested');
            if (refreshOrder) refreshOrder();
        } catch (error) {
            setSubmitError(
                error.response?.data?.error || error.response?.data?.detail || 'Failed to submit return request.'
            );
        }
        setIsSubmitting(false);
    };

    const submitReplaceRequest = async () => {
        if (!reason.trim()) {
            setSubmitError('Please provide a reason.');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(
                `${API_BASE_URL}/order-items/${currentItem.id}/request-replacement/`,
                { reason: reason.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowReplacePopup(false);
            alert('Replacement request submitted successfully!');
            updateItemStatus(currentItem.id, 'Replacement Requested');
            if (refreshOrder) refreshOrder();
        } catch (error) {
            setSubmitError(
                error.response?.data?.error || error.response?.data?.detail || 'Failed to submit replacement request.'
            );
        }
        setIsSubmitting(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h3 className='col-md-6 col-lg-12' >Order Details</h3>
                        {/* Order status badge */}
                   
                        <p className="order-number col-md-6 col-lg-12">#{currentOrder.order_number}</p>
                        <br></br>
                         <span
                        className="status-badge "
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 18px',
                            borderRadius: 28,
                            background: getStatusBgColor(currentOrder.status),
                            color: statusStyle.color,
                            fontWeight: 600,
                            fontSize: '1em',
                            boxShadow: `0 0 8px ${statusStyle.color}66`,
                            userSelect: 'none'
                        }}
                    >
                        <i className={`ph ${statusStyle.icon}`} style={{ marginRight: 8, fontSize: 20 }} />
                        {currentOrder.status}
                    </span>

                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <i className="ph ph-x"></i>
                    </button>
                </div>

                <div className="order-actions-container" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 16, marginBottom: 16 }}>
                    
                    {/* Track Order Button */}
                    <button
                        className='action-btn track-order-btn'
                        type="button"
                        onClick={() => setShowTimeline(true)}
                    >
                        <i className="ph ph-map-trifold" />
                        Track Order
                    </button>

                    {/* Cancel Order Button */}
                    {canCancel && (
                        <button
                            className='action-btn cancel-order-btn'
                            type="button"
                            onClick={() => setShowCancelModal(true)}
                        >
                            <i className="ph ph-x-circle" />
                            Cancel Order
                        </button>
                    )}

                    {/* Return / Replace Buttons / Status Buttons for Items */}
                    {order.status === 'Delivered' &&
                        order.items.map((item, idx) => {
                            // Return button if returnable and active
                            if (item.material?.is_returnable && item.status === 'Active') {
                                return (
                                    <button
                                        key={`return-btn-${idx}`}
                                        className='action-btn return-btn'
                                        type="button"
                                        onClick={() => handleReturnClick(item)}
                                    >
                                        <i className="ph ph-arrow-u-up-left" />
                                        Return
                                    </button>
                                );
                            }
                            // Replace button if replaceable and active
                            if (item.material?.is_replaceable && item.status === 'Active') {
                                return (
                                    <button
                                        key={`replace-btn-${idx}`}
                                        className='action-btn replace-btn'
                                        type="button"
                                        onClick={() => handleReplaceClick(item)}
                                    >
                                        <i className="ph ph-repeat" />
                                        Replace
                                    </button>
                                );
                            }
                            // Item status button if not active
                            return (
                                <button
                                    key={`item-status-btn-${idx}`}
                                    type="button"
                                    className="action-btn status-btn"
                                    onClick={() => handleReplaceClick(item)} // <-- MAKE IT CLICKABLE!
                                >
                                    {item.status}
                                </button>

                            );
                        })}


                </div>

                <div className="modal-body">
                    {/* Order Items Section */}
                    <div className="order-items-section">
                        <h4>Order Items ({currentOrder.items?.length || 0})</h4>
                        {currentOrder.items &&
                            currentOrder.items.map((item, idx) => {
                                const isExpanded = expandedItems[`order-${currentOrder.id}-item-${idx}`];
                                const previewCustomizations = getCustomizationPreview(item.customizations);
                                const totalCustomizations = 1 + (item.customizations?.fields?.length || 0);
                                const hiddenCount = totalCustomizations - previewCustomizations.length;

                                return (
                                    <div
                                        key={idx}
                                        className="order-item-detailed"
                                        style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '12px',
                                            padding: '16px',
                                            marginBottom: '20px',
                                            background: '#fafafa'
                                        }}
                                    >
                                        <div
                                            className="item-number"
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#786cff',
                                                marginBottom: '10px'
                                            }}
                                        >
                                            Item #{idx + 1}
                                        </div>

                                        <div className="item-main-info">
                                            <div className="item-image-section">
                                                <img
                                                    src={getImageUrl(item.material_image)}
                                                    alt={item.material_name}
                                                    className="item-thumb"
                                                />
                                                <button
                                                    className="view-image-btn"
                                                    onClick={() => onViewImage(item.material_image)}
                                                    title="View full image"
                                                >
                                                    <i className="ph ph-eye"></i>
                                                </button>
                                            </div>
                                            <div className="item-details-section">
                                                <h5 className="item-name">{item.product_type_name || 'Product'}</h5>
                                                <p className="item-material">Material: {item.material_name || 'N/A'}</p>
                                                <div className="item-qty-price">
                                                    <span className="quantity">Qty: {item.quantity}</span>
                                                    <span className="price">${parseFloat(item.item_total || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Customizations */}
                                        {item.customizations && (
                                            <div className="item-customizations">
                                                <div className="customizations-preview">
                                                    {previewCustomizations.map((custom) => (
                                                        <div key={custom.id} className="custom-row">
                                                            <span className="custom-label">{custom.label}:</span>
                                                            <span className="custom-value">{custom.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                {isExpanded && hiddenCount > 0 && (
                                                    <div className="customizations-expanded">
                                                        {item.customizations.fields &&
                                                            item.customizations.fields.slice(2).map((field, fieldIdx) => (
                                                                <div key={`expanded-${fieldIdx}`} className="custom-row">
                                                                    <span className="custom-label">{field.name}:</span>
                                                                    <span className="custom-value">{field.selected_value || 'N/A'}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                                {hiddenCount > 0 && (
                                                    <button
                                                        className="btn-view-more-custom"
                                                        onClick={() => toggleExpand(`order-${currentOrder.id}-item-${idx}`)}
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                <i className="ph ph-caret-up"></i>
                                                                View Less
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ph ph-caret-down"></i>+{hiddenCount} more
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {/* Price Breakdown */}
                                        <div className="item-price-breakdown">
                                            <div className="price-detail">
                                                <span>Base:</span>
                                                <span>${parseFloat(item.prices?.base_price || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="price-detail">
                                                <span>Material:</span>
                                                <span>${parseFloat(item.prices?.material_price || 0).toFixed(2)}</span>
                                            </div>
                                            {parseFloat(item.prices?.customization_price || 0) > 0 && (
                                                <div className="price-detail">
                                                    <span>Customization:</span>
                                                    <span>${parseFloat(item.prices?.customization_price || 0).toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="price-detail total">
                                                <span>Unit Price:</span>
                                                <span>${parseFloat(item.prices?.total_price || 0).toFixed(2)}</span>
                                            </div>
                                        </div>

                                        {/* Customization Image Section */}
                                        <div className="order-item-customization-image mt-3">
                                            <div className="customization-image-section">
                                                {item.customization_image_url ? (
                                                    <div className="customization-image-card">
                                                        <div className="image-thumbnail">
                                                            <img src={getImageUrl(item.customization_image_url)} alt="Customization" />
                                                        </div>
                                                        <div className="image-info">
                                                            <h5 className="image-title">Customization Image</h5>
                                                            <div className="image-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn-preview"
                                                                    onClick={() => onViewImage(item.customization_image_url)}
                                                                >
                                                                    <i className="ph ph-eye"></i>
                                                                    <span>Preview Image</span>
                                                                </button>
                                                                {canCancel && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn-edit"
                                                                        onClick={() => setEditingOrderItemId(item.id)}
                                                                    >
                                                                        <i className="ph ph-pencil"></i>
                                                                        <span>Edit Image</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : canCancel ? (
                                                    <div className="customization-image-upload">
                                                        <div className="upload-prompt">
                                                            <i className="ph ph-image"></i>
                                                            <h5 className="upload-title">No Customization Image</h5>
                                                            <p className="upload-description">Upload your custom design for this item</p>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                id={`upload-file-input-${item.id}`}
                                                                style={{ display: 'none' }}
                                                                onChange={async (e) => {
                                                                    const file = e.target.files[0];
                                                                    if (file) {
                                                                        await handleOrderItemImageSave(currentOrder, item, file);
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn-upload-initial"
                                                                onClick={() =>
                                                                    document.getElementById(`upload-file-input-${item.id}`).click()
                                                                }
                                                                disabled={orderUploadingImages[item.id]}
                                                            >
                                                                {orderUploadingImages[item.id] ? (
                                                                    <>
                                                                        <i className="ph ph-spinner spinner-icon"></i>
                                                                        <span>Uploading...</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="ph ph-upload-simple"></i>
                                                                        <span>Upload Image</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="customization-image-empty">
                                                        <div className="empty-message">
                                                            <i className="ph ph-image-slash"></i>
                                                            <p>No customization image provided</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Image Editor */}
                                        {editingOrderItemId === item.id && (
                                            <div className="image-editor-section">
                                                <div className="editor-content">
                                                    <label className="file-upload-label">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="file-input-hidden"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    const reader = new FileReader();
                                                                    reader.onloadend = () => {
                                                                        setOrderItemNewImages((prev) => ({
                                                                            ...prev,
                                                                            [item.id]: { file, preview: reader.result }
                                                                        }));
                                                                    };
                                                                    reader.readAsDataURL(file);
                                                                }
                                                            }}
                                                        />
                                                        <div className="upload-button">
                                                            <i className="ph ph-upload-simple"></i>
                                                            <span>Choose New Image</span>
                                                        </div>
                                                    </label>
                                                    {orderItemNewImages[item.id]?.preview && (
                                                        <div className="image-preview">
                                                            <img src={orderItemNewImages[item.id].preview} alt="Preview" />
                                                        </div>
                                                    )}
                                                    <div className="editor-actions">
                                                        <button
                                                            className="btn-save"
                                                            disabled={!orderItemNewImages[item.id]?.file}
                                                            onClick={async () => {
                                                                if (orderItemNewImages[item.id]?.file) {
                                                                    await handleOrderItemImageSave(
                                                                        currentOrder,
                                                                        item,
                                                                        orderItemNewImages[item.id].file
                                                                    );
                                                                    setEditingOrderItemId(null);
                                                                    setOrderItemNewImages((prev) => {
                                                                        const newState = { ...prev };
                                                                        delete newState[item.id];
                                                                        return newState;
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <i className="ph ph-check"></i>
                                                            <span>Save Changes</span>
                                                        </button>
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={() => {
                                                                setEditingOrderItemId(null);
                                                                setOrderItemNewImages((prev) => {
                                                                    const newState = { ...prev };
                                                                    delete newState[item.id];
                                                                    return newState;
                                                                });
                                                            }}
                                                        >
                                                            <i className="ph ph-x"></i>
                                                            <span>Cancel</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>

                    {/* Price Summary Section */}
                    <div className="price-summary-section">
                        <h4>Price Summary</h4>
                        <div className="summary-rows">
                            <div className="summary-row">
                                <span>Subtotal:</span>
                                <span>${parseFloat(currentOrder.subtotal).toFixed(2)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Tax (13%):</span>
                                <span>${parseFloat(currentOrder.tax).toFixed(2)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Shipping:</span>
                                <span>${parseFloat(currentOrder.shipping).toFixed(2)}</span>
                            </div>
                            {currentOrder.discount_amount > 0 && (
                                <div className="summary-row discount">
                                    <span>Discount:</span>
                                    <span>-${parseFloat(currentOrder.discount_amount).toFixed(2)}</span>
                                </div>
                            )}
                            <div className="summary-row total">
                                <span>Total:</span>
                                <span>${parseFloat(currentOrder.total_amount).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Delivery Address Section */}
                    <div className="delivery-address-section">
                        <h4>Delivery Address</h4>
                        <div className="address-box">
                            {currentOrder.address && currentOrder.address.address_data ? (
                                <p>
                                    <strong>{currentOrder.address.address_data.fullName}</strong>
                                    {currentOrder.address.address_data.company && (
                                        <>
                                            <br />
                                            {currentOrder.address.address_data.company}
                                        </>
                                    )}
                                    <br />
                                    {currentOrder.address.address_data.streetAddress}
                                    <br />
                                    {currentOrder.address.address_data.city}, {currentOrder.address.address_data.province}{' '}
                                    {currentOrder.address.address_data.postalCode}
                                    <br />
                                    Phone: {currentOrder.address.address_data.phone}
                                    {currentOrder.address.address_data.email && (
                                        <>
                                            <br />
                                            Email: {currentOrder.address.address_data.email}
                                        </>
                                    )}
                                    {currentOrder.address.address_data.deliveryNotes && (
                                        <>
                                            <br />
                                            <em className="delivery-notes">Note: {currentOrder.address.address_data.deliveryNotes}</em>
                                        </>
                                    )}
                                </p>
                            ) : (
                                <p className="text-gray-500">No delivery address available</p>
                            )}
                        </div>
                    </div>
                    {order.status === 'Delivered' && currentOrder.items && currentOrder.items.map((item) => (
                        <div key={item.id} className="order-item-box">
                            {/* ...Other item info, such as product and material... */}

                            {/* Customer Review Section */}
                            <div className="customer-review-section">
                                {loadingReviews[item.id] ? (
                                    <div className="rate-order-prompt" style={{ padding: '20px', textAlign: 'center' }}>
                                        <div className="spinner" style={{
                                            width: '30px',
                                            height: '30px',
                                            border: '3px solid #f3f4f6',
                                            borderTop: '3px solid #8b5cf6',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            margin: '0 auto'
                                        }}></div>
                                        <p style={{ marginTop: '10px', color: '#6b7280' }}>Loading review...</p>
                                    </div>
                                ) : submittedReviews[item.id] ? (
                                    // Display submitted review
                                    <div className="submitted-review-display">
                                        <div className="review-header">
                                            <h4>Your Review</h4>
                                            <button
                                                className="edit-review-btn"
                                                onClick={() => {
                                                    setCurrentReviewItem(item);
                                                    setShowRatingModal(true);
                                                }}
                                            >
                                                <i className="ph ph-pencil-simple"></i>
                                                Edit
                                            </button>
                                        </div>

                                        <div className="review-stars-display">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <i
                                                    key={star}
                                                    className={star <= submittedReviews[item.id].rating ? 'ph-fill ph-star' : 'ph ph-star'}
                                                    style={{
                                                        color: star <= submittedReviews[item.id].rating ? '#fbbf24' : '#d1d5db',
                                                        fontSize: '24px'
                                                    }}
                                                ></i>
                                            ))}
                                            <span className="rating-value">({submittedReviews[item.id].rating}/5)</span>
                                        </div>

                                        <div className="review-description-display">
                                            <p>{submittedReviews[item.id].description}</p>
                                        </div>

                                        {submittedReviews[item.id].images && submittedReviews[item.id].images.length > 0 && (
                                            <div className="review-images-display">
                                                <h5>Photos</h5>
                                                <div className="review-images-grid">
                                                    {submittedReviews[item.id].images.map((img, idx) => (
                                                        <div key={idx} className="review-image-item" onClick={() => onViewImage(img)}>
                                                            <img src={img} alt={`Review ${idx + 1}`} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="review-footer">
                                            <span className="review-date">
                                                <i className="ph ph-calendar"></i>
                                                Reviewed on {formatDate(submittedReviews[item.id].date)}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    // Show Rate Item button
                                    <div className="rate-order-prompt">
                                        <h4>Rate This Item</h4>
                                        <p>Share your experience to help us improve</p>
                                        <button
                                            className="rate-order-btn"
                                            onClick={() => {
                                                setCurrentReviewItem(item);
                                                setShowRatingModal(true);
                                            }}
                                        >
                                            <i className="ph ph-star"></i>
                                            Write a Review
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}



                    {/* Cancellation Details */}
                    {currentOrder.status === 'Cancelled' && currentOrder.cancellation_reason && (
                        <div className="cancellation-details-section">
                            <h4>Cancellation Information</h4>
                            <div className="cancellation-box">
                                <div className="cancellation-info">
                                    <span className="label">Cancelled At:</span>
                                    <span className="value">{formatDate(currentOrder.cancelled_at)}</span>
                                </div>
                                <div className="cancellation-reason">
                                    <span className="label">Reason:</span>
                                    <p className="reason-text">{currentOrder.cancellation_reason}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn-outline-main" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            {/* Modals */}
            <OrderTimelineModal
                order={currentOrder}
                isOpen={showTimeline}
                onClose={() => setShowTimeline(false)}
                statusSteps={statusSteps}
            />

            <CancelOrderModal
                order={currentOrder}
                isOpen={showCancelModal}
                cancellationReason={cancellationReason}
                setCancellationReason={setCancellationReason}
                isCancelling={isCancelling}
                cancelError={cancelError}
                setCancelError={setCancelError}
                onConfirm={handleCancelOrder}
                onClose={() => setShowCancelModal(false)}
            />

            <ReturnReplaceModal
                type="return"
                item={currentItem}
                order={currentOrder}
                isOpen={showReturnPopup}
                reason={reason}
                setReason={setReason}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submitReturnRequest}
                onClose={handleCloseReturnPopup}
            />

            <ReturnReplaceModal
                type="replace"
                item={currentItem}
                order={currentOrder}
                isOpen={showReplacePopup}
                reason={reason}
                setReason={setReason}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submitReplaceRequest}
                onClose={handleCloseReplacePopup}
            />

            {showRatingModal && currentReviewItem && (
                <RatingModal
                    orderItem={currentReviewItem}
                    order={currentOrder}
                    onClose={() => { setShowRatingModal(false); setCurrentReviewItem(null); }}
                    onSubmit={(reviewData) => handleReviewSubmitted(currentReviewItem.id, reviewData)}
                    token={token}
                    apiBaseUrl={API_BASE_URL}
                    existingReview={submittedReviews[currentReviewItem.id]}
                />
            )}

        </div>
    );
}
